import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffectOnActive } from 'keepalive-for-react';
import { Heart, NavigationArrow, BookOpen, Plus, Path } from '@phosphor-icons/react';
import { useFavorites } from '../context/FavoritesContext';
import { useCollections } from '../context/CollectionsContext';
import PlaceCard from '../components/PlaceCard';
import PlaceCardSkeleton from '../components/PlaceCardSkeleton';
import Tabs from '../components/Tabs';
import EmptyState from '../components/EmptyState';
import RouteCard from '../components/RouteCard';
import UserRouteCard, { getRouteProgress } from '../components/UserRouteCard';
import CollectionCard from '../components/CollectionCard';
import CreateRouteWizard from '../components/CreateRouteWizard';
import BottomSheet from '../components/BottomSheet';
import telegramService from '../services/telegram';
import notify, { notifyIcons } from '../utils/notify';
import { deleteUserRoute, removeFromSavedRoutes, removeFromSavedCollections } from '../services/api';

// Сводка в профиле ведёт сразу во вкладку: /favorites?tab=routes
const TAB_IDS = ['places', 'routes', 'collections'];

const MAX_USER_ROUTES = 5;

/** Скелетон плашки маршрута или подборки — тот же каркас, что у самой плашки */
const CardSkeleton = () => (
  <div className="mb-2 flex items-center gap-3 overflow-hidden rounded-lg border border-theme-border bg-theme-card p-3">
    <div className="animate-shimmer h-[72px] w-[72px] flex-shrink-0 rounded-md" />
    <div className="min-w-0 flex-1">
      <div className="h-4 w-2/3 rounded-sm bg-theme-card-secondary" />
      <div className="mt-2 h-3 w-1/3 rounded-sm bg-theme-card-secondary" />
    </div>
  </div>
);

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { favorites, loading: favLoading, loadFavorites } = useFavorites();
  // Списки берутся из контекста, а не грузятся сюда своей копией: иначе
  // удалённая на своей странице подборка оставалась бы висеть на вкладке
  // до её переключения — страница лежит в LRU-кеше и состояние переживает
  // уход с экрана
  const {
    savedRoutes,
    userRoutes,
    savedCollections,
    loading: savedLoading,
    loadSaved,
    removeSavedRoute,
    removeSavedCollection,
    removeUserRoute,
  } = useCollections();

  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'places'
  );
  const [routesSubTab, setRoutesSubTab] = useState('personal');
  const [showWizard, setShowWizard] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showNoFavoritesModal, setShowNoFavoritesModal] = useState(false);
  // Личный маршрут удаляется насовсем, поэтому спрашиваем. Сохранённые
  // маршрут и подборка убираются из списка без вопроса: они остаются
  // в каталоге, и вернуть их — одно нажатие
  const [routeToDelete, setRouteToDelete] = useState(null);

  const tryOpenWizard = () => {
    telegramService.hapticFeedback('light');
    if (userRoutes.length >= MAX_USER_ROUTES) {
      setShowLimitModal(true);
      return;
    }
    if (favorites.length < 2) {
      setShowNoFavoritesModal(true);
      return;
    }
    // Панель открывается сразу, данные обновляются тихо: background не даёт
    // переключить общий loading, иначе страница под панелью подменит список
    // лоадером и спрячет кнопку — это и было мерцанием
    setWizardKey(k => k + 1);
    setShowWizard(true);
    loadFavorites({ background: true });
  };

  // Удаление личного маршрута — необратимое: подтверждаем панелью
  const confirmDeleteRoute = async () => {
    const route = routeToDelete;
    if (!route) return;
    setRouteToDelete(null);

    // Список правится сразу, не дожидаясь перезагрузки с сервера:
    // операция подтверждена, а счётчик во вкладке должен измениться тут же
    try {
      await deleteUserRoute(route.id);
      removeUserRoute(route.id);
      // Прогресс прохождения живёт отдельно от записи — если его не снять,
      // следующий маршрут с тем же id унаследует чужие пройденные точки
      localStorage.removeItem(`user_route_progress_${route.id}`);
      telegramService.hapticFeedback('success');
      notify('Маршрут удалён', { icon: notifyIcons.done });
    } catch {
      notify.error('Не удалось удалить. Проверьте связь');
    }
  };

  // Убрать из сохранённого — обратимое, поэтому без подтверждения,
  // но с возвратом: спрашивать на каждое снятие закладки утомительно
  const unsaveRoute = async (savedRoute) => {
    try {
      await removeFromSavedRoutes(savedRoute.id);
      removeSavedRoute(savedRoute.id);
      notify('Маршрут убран из сохранённых', { icon: notifyIcons.favoriteRemoved });
    } catch {
      notify.error('Не удалось убрать. Проверьте связь');
    }
  };

  const unsaveCollection = async (savedCollection) => {
    try {
      await removeFromSavedCollections(savedCollection.id);
      removeSavedCollection(savedCollection.id);
      notify('Подборка убрана из сохранённых', { icon: notifyIcons.favoriteRemoved });
    } catch {
      notify.error('Не удалось убрать. Проверьте связь');
    }
  };

  // Страница живёт в кеше: при повторном заходе с другим ?tab= состояние
  // осталось бы от прошлого визита, и сводка в профиле вела бы не туда
  useEffect(() => {
    if (TAB_IDS.includes(tabFromUrl)) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  // Тихое обновление при возврате: правки с этого устройства уже в контексте,
  // но список мог измениться с другого. background не даёт подменить
  // показанное лоадером
  useEffectOnActive((active) => {
    if (active) loadSaved({ background: true });
  }, [loadSaved]);

  // Число переехало во вкладку: строка «Всего: 6 мест» дублировала шесть
  // видимых карточек и стояла только на одной вкладке из трёх. Здесь оно
  // видно до перехода
  const tabs = [
    { id: 'places', label: 'Места', count: favorites.length },
    { id: 'routes', label: 'Маршруты', count: userRoutes.length + savedRoutes.length },
    { id: 'collections', label: 'Подборки', count: savedCollections.length },
  ];

  const goHome = () => { sessionStorage.removeItem('scroll-/'); navigate('/'); };

  return (
    <div className="min-h-screen bg-theme-bg pb-6">
      <section
        className="px-4 pb-4"
        style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
      >
        <h1 className="mb-3 text-h2 text-theme-text-primary">Мои сохранения</h1>
        <Tabs tabs={tabs} activeId={activeTab} onChange={setActiveTab} />
      </section>

      <div className="px-4">

        {/* ── МЕСТА ── */}
        {/* Кнопки создания маршрута здесь нет: она осталась на вкладке
            «Маршруты», где маршруты и живут. Лента сохранённого начинается
            сразу с карточек, а не с призыва заняться другим */}
        {activeTab === 'places' && (
          <>
            {favLoading && (
              <div className="grid grid-cols-2 gap-4">
                {[0, 1, 2, 3].map(i => <PlaceCardSkeleton key={i} />)}
              </div>
            )}
            {!favLoading && favorites.length === 0 && (
              <EmptyState
                icon={Heart}
                title="Здесь пока пусто"
                description="Сохранённые места появятся тут"
                actionLabel="Найти места"
                onAction={goHome}
              />
            )}
            {!favLoading && favorites.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {favorites.map(fav => <PlaceCard key={fav.id} place={fav.place} />)}
              </div>
            )}
          </>
        )}

        {/* ── МАРШРУТЫ ── */}
        {activeTab === 'routes' && (
          <>
            <Tabs
              sub
              className="mb-4"
              tabs={[{ id: 'personal', label: 'Личные' }, { id: 'saved', label: 'Сохранённые' }]}
              activeId={routesSubTab}
              onChange={setRoutesSubTab}
            />

            {routesSubTab === 'personal' && (
              <>
                {/* Пока маршрутов нет, кнопку даёт пустое состояние ниже —
                    раньше две одинаковые «Создать маршрут» стояли подряд */}
                {(savedLoading || userRoutes.length > 0) && (
                <button
                  onClick={tryOpenWizard}
                  className="mb-4 flex h-12 w-full items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-colors duration-fast ease-out active:scale-98"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus size={18} weight="bold" />
                  Создать маршрут
                </button>
                )}

                {savedLoading && <><CardSkeleton /><CardSkeleton /></>}
                {!savedLoading && userRoutes.length === 0 && (
                  <EmptyState
                    icon={Path}
                    title="Нет личных маршрутов"
                    description="Соберите маршрут из сохранённых мест"
                    actionLabel="Создать маршрут"
                    onAction={tryOpenWizard}
                  />
                )}
                {!savedLoading && userRoutes.length > 0 && (
                  // Начатые вперёд, пройденные в конец: продолжить прогулку
                  // хотят чаще, чем перечитать законченную
                  [...userRoutes].sort((a, b) => {
                    const pa = getRouteProgress(a.id, (a.stops || []).length);
                    const pb = getRouteProgress(b.id, (b.stops || []).length);
                    const rank = p => (p ? (p.allDone ? 2 : 0) : 1);
                    return rank(pa) - rank(pb);
                  }).map(ur => (
                    <UserRouteCard
                      key={ur.id}
                      userRoute={ur}
                      onOpen={() => navigate(`/my-route/${ur.id}/walk`)}
                      onDelete={() => setRouteToDelete(ur)}
                    />
                  ))
                )}
              </>
            )}

            {routesSubTab === 'saved' && (
              <>
                {savedLoading && <><CardSkeleton /><CardSkeleton /></>}
                {!savedLoading && savedRoutes.length === 0 && (
                  <EmptyState
                    icon={NavigationArrow}
                    title="Нет сохранённых маршрутов"
                    description="Сохраняйте маршруты, чтобы пройти их позже"
                    actionLabel="Смотреть маршруты"
                    onAction={() => navigate('/routes')}
                  />
                )}
                {!savedLoading && savedRoutes.map(sr => (
                  <RouteCard key={sr.id} route={sr.route} onUnsave={() => unsaveRoute(sr)} />
                ))}
              </>
            )}
          </>
        )}

        {/* ── ПОДБОРКИ ── */}
        {activeTab === 'collections' && (
          <>
            {savedLoading && <><CardSkeleton /><CardSkeleton /></>}
            {!savedLoading && savedCollections.length === 0 && (
              <EmptyState
                icon={BookOpen}
                title="Нет сохранённых подборок"
                description="Сохраняйте подборки мест, чтобы вернуться к ним позже"
                actionLabel="Смотреть подборки"
                onAction={() => navigate('/category')}
              />
            )}
            {!savedLoading && savedCollections.map(sc => (
              <CollectionCard
                key={sc.id}
                collection={sc.collection}
                onUnsave={() => unsaveCollection(sc)}
              />
            ))}
          </>
        )}
      </div>

      {/* ── МОДАЛКА НЕДОСТАТОЧНО ИЗБРАННОГО ── */}
      <BottomSheet
        isOpen={showNoFavoritesModal}
        onClose={() => setShowNoFavoritesModal(false)}
        title="Недостаточно избранных мест"
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => setShowNoFavoritesModal(false)}
              className="flex-1 h-12 rounded-md text-label transition-colors duration-fast ease-out active:scale-98"
              style={{
                background: 'var(--color-card-secondary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Закрыть
            </button>
            <button
              onClick={() => { setShowNoFavoritesModal(false); goHome(); }}
              className="flex-1 h-12 rounded-md text-label bg-action text-on-action transition-colors duration-fast ease-out active:scale-98"
            >
              Найти места
            </button>
          </div>
        }
      >
        <p className="text-body-sm text-theme-text-secondary">
          Для создания маршрута нужно минимум 2 сохранённых места. Добавьте места
          в избранное и попробуйте снова.
        </p>
      </BottomSheet>

      {/* ── ПОДТВЕРЖДЕНИЕ УДАЛЕНИЯ ЛИЧНОГО МАРШРУТА ── */}
      <BottomSheet
        isOpen={Boolean(routeToDelete)}
        onClose={() => setRouteToDelete(null)}
        title="Удалить маршрут?"
        // Необратимое действие: тап мимо закрыл бы панель слишком легко
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => setRouteToDelete(null)}
              className="flex-1 h-12 rounded-md text-label transition-colors duration-fast ease-out active:scale-98"
              style={{
                background: 'var(--color-card-secondary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Отмена
            </button>
            <button
              onClick={confirmDeleteRoute}
              className="flex-1 h-12 rounded-md text-label border-0 transition-colors duration-fast ease-out active:scale-98"
              style={{ background: 'var(--color-danger)', color: 'var(--color-on-semantic)' }}
            >
              Удалить
            </button>
          </div>
        }
      >
        <p className="text-body-sm text-theme-text-secondary">
          «{routeToDelete?.title}» будет удалён вместе с прогрессом
          прохождения. Это действие нельзя отменить.
        </p>
      </BottomSheet>

      {/* ── МОДАЛКА ЛИМИТА ── */}
      <BottomSheet
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title="Достигнут лимит маршрутов"
        footer={
          <button
            onClick={() => { setShowLimitModal(false); setActiveTab('routes'); setRoutesSubTab('personal'); }}
            className="w-full h-12 rounded-md text-label bg-action text-on-action transition-colors duration-fast ease-out active:scale-98"
          >
            Мои маршруты
          </button>
        }
      >
        <p className="text-body-sm text-theme-text-secondary">
          Вы можете хранить не более {MAX_USER_ROUTES} маршрутов. Удалите один
          из существующих, чтобы создать новый.
        </p>
      </BottomSheet>

      {/* ── WIZARD ── */}
      {showWizard && (
        <CreateRouteWizard
          key={wizardKey}
          favorites={favorites}
          onClose={() => setShowWizard(false)}
          onCreated={() => setShowWizard(false)}
        />
      )}
    </div>
  );
};

export default FavoritesPage;
